/**
 * Demo fleet for ?mock=1, built from the demo machines: automatic updates on (stable channel), a
 * newer release from a (demo) signed release feed rolling out in stages (the canary is updated
 * and soaking), one machine that updates by hand, a newer beta release nobody follows yet,
 * quiet notices from the last rollout, and simulated progress so every state can be seen and tried.
 */
import { DEFAULT_UPDATE_POLICY, compareVersions, stageName } from '@orgus/protocol';
import type { AgentRelease, FleetMachine, FleetNotice, FleetState, Machine, Rollout, UpdateHold } from '@orgus/protocol';

const H = 3_600_000;
const D = 24 * H;

const release = (version: string, at: number, channel: 'stable' | 'beta' = 'stable'): AgentRelease => ({
  version,
  sha256: version.replace(/\./g, '').padEnd(64, 'a7'),
  size: 214_380 + version.length * 1000,
  createdAt: at,
  publishedBy: 'Orgus release signing',
  channel,
});

export function mockFleet(machines: Machine[], now = Date.now()): FleetState {
  const [first, , third] = machines;
  const releases = [release('0.1.2', now - 5 * H, 'beta'), release('0.1.1', now - 2 * H), release('0.1.0', now - 9 * D)];
  const latest = '0.1.1';
  const policy = { ...DEFAULT_UPDATE_POLICY, updatedAt: now - 20 * D, updatedBy: 'owner' };
  const soakUntil = now + 40 * 60_000;
  const behind = machines.filter((m) => compareVersions(m.agentVersion, latest) < 0);
  const rollout: Rollout | null = first
    ? {
        id: 'ro_demo',
        version: latest,
        startedBy: 'policy',
        startedAt: now - 25 * 60_000,
        stage: 'test',
        status: 'running',
        plan: { stages: policy.stages, wave: 0, soakUntil, haltFailureRate: policy.haltFailureRate, haltMinFailures: policy.haltMinFailures, channel: 'stable' },
        targets: behind.map(
          (m) =>
            ({
              machineId: m.id,
              ring: m.id === first.id ? 'test' : 'fleet',
              from: m.agentVersion,
              state: m.id === first.id ? 'done' : m.id === third?.id ? 'skipped' : 'pending',
              ...(m.id === third?.id ? { message: 'Runs from a source checkout. Update it there (git pull), then restart it.' } : { wave: m.id === first.id ? 0 : 2 }),
              at: now - 20 * 60_000,
            }) as Rollout['targets'][number],
        ),
      }
    : null;
  const waits: UpdateHold = { reason: 'stage', detail: `It is in stage 3 of 3 (${stageName(policy.stages, 2).toLowerCase()}), which starts after stage 1 has soaked.`, until: soakUntil };
  const fm: FleetMachine[] = machines.map((m) => {
    const isFirst = m.id === first?.id;
    const isThird = m.id === third?.id;
    // Nobody follows beta in the demo; the drawer's channel switch moves a machine there.
    const isBeta = false;
    const due = latest;
    const behindIt = compareVersions(isFirst ? latest : m.agentVersion, due) < 0;
    return {
      machineId: m.id,
      ring: isFirst ? 'test' : 'fleet',
      version: isFirst ? latest : m.agentVersion,
      drift: behindIt ? 'behind' : 'current',
      updatable: !isThird,
      why: isThird ? 'Runs from a source checkout. Update it there (git pull), then restart it.' : null,
      service: m.os === 'windows' ? 'task-scheduler' : m.os === 'macos' ? 'launchd' : isThird ? 'source' : 'systemd',
      serviceEntry: m.os === 'windows' ? `OrgusNodeAgent-${m.name.toUpperCase()}-owner` : m.os === 'macos' ? 'com.orgus.node-agent' : isThird ? null : 'orgus-node-agent.service',
      update: isFirst ? { rolloutId: 'ro_demo', version: latest, stage: 'done', at: now - 18 * 60_000 } : null,
      rolledBackFrom: null,
      busy: { jobs: 0, runs: 0 },
      statusAt: now - 5_000,
      channel: isBeta ? 'beta' : 'stable',
      channelOverride: isBeta ? 'beta' : null,
      keys: isThird ? null : 'release',
      hold: !behindIt ? null : isThird ? { reason: 'not-updatable', detail: 'Runs from a source checkout. Update it there (git pull), then restart it.' } : isBeta ? { reason: 'queued', detail: 'It is next, after the rollout of agent 0.1.1 ends.' } : waits,
    };
  });
  const notices: FleetNotice[] = [
    { id: 'ro_old:done', at: now - 9 * D + 2 * H, tone: 'success', title: 'Updated to 0.1.0 on 2 machines', version: '0.1.0', rolloutId: 'ro_old' },
  ];
  return {
    feed: 'Signed release feed (demo)',
    feedKeys: 'release',
    feedProblems: [],
    releases,
    latest,
    rollout,
    history: [
      {
        id: 'ro_old',
        version: '0.1.0',
        startedBy: 'policy',
        startedAt: now - 9 * D + H,
        stage: 'fleet',
        status: 'done',
        targets: machines.slice(0, 2).map((m) => ({ machineId: m.id, ring: 'fleet', from: '0.0.9', state: 'done', at: now - 9 * D + 2 * H })),
        endedAt: now - 9 * D + 2 * H,
      },
    ],
    machines: fm,
    policy,
    auto: { on: true, next: latest, hold: null },
    notices,
  };
}
